import React, { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, X } from "lucide-react";
import { PORTFOLIO, CATEGORIES } from "../data/content";

function Card({ item, onOpen, i }) {
  const [tilt, setTilt] = useState({ rx: 0, ry: 0 });
  const handleMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    const x = (e.clientX - r.left) / r.width - 0.5;
    const y = (e.clientY - r.top) / r.height - 0.5;
    setTilt({ rx: -y * 8, ry: x * 10 });
  };
  const reset = () => setTilt({ rx: 0, ry: 0 });

  return (
    <motion.button
      data-testid={`portfolio-card-${item.id}`}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.8, delay: i * 0.06, ease: [0.7, 0, 0.2, 1] }}
      onClick={() => onOpen(item)}
      onMouseMove={handleMove}
      onMouseLeave={reset}
      className={`group relative col-span-1 ${item.span || ""} block w-full text-left overflow-hidden rounded-sm bg-[#12141D] aspect-[4/5] lg:aspect-auto lg:min-h-[420px]`}
      style={{ perspective: 1200 }}
    >
      <motion.div
        animate={{ rotateX: tilt.rx, rotateY: tilt.ry }}
        transition={{ type: "spring", stiffness: 120, damping: 14 }}
        className="absolute inset-0"
        style={{ transformStyle: "preserve-3d" }}
      >
        <img
          src={item.image}
          alt={item.title}
          loading="lazy"
          className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1.6s] group-hover:scale-[1.06]"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0B0E14]/85 via-[#0B0E14]/10 to-transparent" />
        <div className="absolute inset-0 grain-overlay" />

        <div className="absolute top-5 left-5 flex items-center gap-2">
          <span className="rounded-full bg-[#0B0E14]/60 backdrop-blur px-3 py-1 text-[10px] uppercase tracking-overline text-[#D18A58] border border-[#D18A58]/30">
            {item.category}
          </span>
        </div>

        <div className="absolute bottom-5 left-5 right-5 z-10">
          <div className="flex items-end justify-between gap-4">
            <div>
              <div className="text-[10px] uppercase tracking-overline text-[#C5A29B] mb-2">
                {item.year} · {item.role}
              </div>
              <h3 className="font-display text-2xl md:text-3xl text-[#F2EBE5] leading-tight">
                {item.title}
              </h3>
            </div>
            <div className="h-10 w-10 rounded-full border border-[#F2EBE5]/30 flex items-center justify-center text-[#F2EBE5] transition-all group-hover:bg-[#D18A58] group-hover:border-[#D18A58] group-hover:text-[#0B0E14]">
              <ArrowUpRight className="h-4 w-4" />
            </div>
          </div>
        </div>
      </motion.div>
    </motion.button>
  );
}

export default function Portfolio() {
  const [filter, setFilter] = useState("All");
  const [open, setOpen] = useState(null);

  const list = useMemo(
    () => (filter === "All" ? PORTFOLIO : PORTFOLIO.filter((p) => p.category === filter)),
    [filter]
  );

  return (
    <section
      id="portfolio"
      data-testid="portfolio-section"
      className="relative bg-[#0B0E14] text-[#F2EBE5] py-24 md:py-36"
    >
      <div className="mx-auto max-w-7xl px-6 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-12 md:mb-20">
          <div>
            <div className="flex items-center gap-3 text-[10px] tracking-overline uppercase text-[#D18A58] mb-5">
              <span className="h-px w-10 bg-[#D18A58]" />
              Selected Work · 01
            </div>
            <h2 className="font-display text-5xl sm:text-6xl md:text-7xl leading-[0.95] tracking-tight">
              The <span className="italic text-[#D18A58]">archive</span>,<br/>
              curated.
            </h2>
          </div>
          <p className="max-w-md text-sm md:text-base text-[#C5A29B] leading-relaxed">
            A mix of editorial direction, styling and conceptual fashion illustration — each project an exercise in restraint and intent.
          </p>
        </div>

        <div className="flex gap-2 md:gap-3 mb-10 md:mb-14 overflow-x-auto scrollbar-hide -mx-6 px-6">
          {CATEGORIES.map((c) => (
            <button
              key={c}
              data-testid={`filter-btn-${c.toLowerCase()}`}
              onClick={() => setFilter(c)}
              className={`shrink-0 rounded-full border px-5 py-2.5 text-[11px] uppercase tracking-overline transition-all ${
                filter === c
                  ? "bg-[#D18A58] text-[#0B0E14] border-[#D18A58]"
                  : "border-[#F2EBE5]/15 text-[#F2EBE5]/80 hover:border-[#D18A58]/60 hover:text-[#F2EBE5]"
              }`}
            >
              {c}
            </button>
          ))}
        </div>

        <motion.div layout className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-4 md:gap-6">
          <AnimatePresence mode="popLayout">
            {list.map((item, i) => (
              <Card key={item.id} item={item} onOpen={setOpen} i={i} />
            ))}
          </AnimatePresence>
        </motion.div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="portfolio-detail-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] bg-[#0B0E14]/95 backdrop-blur-2xl flex items-center justify-center p-4 md:p-10"
            onClick={() => setOpen(null)}
          >
            <motion.div
              initial={{ scale: 0.94, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.94, y: 20 }}
              transition={{ duration: 0.5, ease: [0.7, 0, 0.2, 1] }}
              className="relative grid md:grid-cols-2 gap-6 md:gap-10 max-w-6xl w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                data-testid="portfolio-close-btn"
                onClick={() => setOpen(null)}
                className="absolute top-2 right-2 md:-top-4 md:-right-4 z-10 h-11 w-11 rounded-full bg-[#D18A58] text-[#0B0E14] flex items-center justify-center"
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>
              <img src={open.image} alt={open.title} className="w-full h-auto rounded-sm object-cover" />
              <div className="md:py-12">
                <div className="text-[10px] uppercase tracking-overline text-[#D18A58] mb-4">
                  {open.category} · {open.year}
                </div>
                <h3 className="font-display text-4xl md:text-5xl leading-tight mb-6 text-[#F2EBE5]">
                  {open.title}
                </h3>
                <p className="text-[#C5A29B] text-base md:text-lg leading-relaxed mb-8">
                  {open.description}
                </p>
                <div className="border-t border-white/10 pt-6 text-sm text-[#8A8D98] space-y-2">
                  <div className="flex justify-between"><span>Role</span><span className="text-[#F2EBE5]">{open.role}</span></div>
                  <div className="flex justify-between"><span>Year</span><span className="text-[#F2EBE5]">{open.year}</span></div>
                  <div className="flex justify-between"><span>Category</span><span className="text-[#F2EBE5]">{open.category}</span></div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
